import { useEffect, useRef, useState } from "react";
import { useAppStore } from "../stores/useAppStore";
import IconArrow from "./IconArrow";
import Button from "./Button";
import { motion } from "motion/react";
import AnalyticsWrapper from "./AnalyticsWrapper";
import { toUrlFriendly } from "../utils/utils";
import IconExpand from "./IconExpand";


export default function Viewer({item}) {
  const { content, cardTouchPos } = useAppStore();
  const { currentIndex: carouselIndex } = useAppStore((state) => state.carousel);
  const { closeViewer } = useAppStore((state) => state.viewer);
  const { fullScreen, toggleFullScreen, setCardTouchPos } = useAppStore();

  const [isLoading, setIsLoading] = useState(true); // Estado para controlar el loading
  const [showVideoOverlay, setShowVideoOverlay] = useState(true); // Estado para controlar el overlay del video
  
  const iframeRef = useRef(null);
  const videoRef = useRef(null);

  const handleIframeLoad = () => {
    // Pequeño delay para asegurar que el contenido esté completamente renderizado
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleVideoLoad = () => {
    // Pequeño delay para asegurar que el contenido esté completamente renderizado
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleVideoPlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowVideoOverlay(false);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleVideoPauseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setShowVideoOverlay(true);
    }
  };

  const handleVideoEnded = () => {
    setShowVideoOverlay(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleExpandToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFullScreen();
  };

  useEffect(() => {
    const urlFriendlyTitle = toUrlFriendly(item.title);
    const subsection = toUrlFriendly(content?.lets_talk_strategy?.items[carouselIndex]?.category);
    AnalyticsWrapper.trackPageView(`/lets_talk_strategy/${subsection}/${urlFriendlyTitle}`);
  }, [item.title]);

  const handleButtonBackClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    let x, y;

    // Para eventos táctiles, usar changedTouches en lugar de touches
    if (event.changedTouches && event.changedTouches.length > 0) {
      x = event.changedTouches[0].clientX;
      y = event.changedTouches[0].clientY;
    } else if (event.touches && event.touches.length > 0) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});

    closeViewer();
  };

  const variants = {
    closed: {
        backgroundColor: '#1a2951',
        clipPath: `circle(0 at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        pointerEvents: 'none',
        transition: {
            duration: 0.5,
            ease: "easeInOut",
            delay: 0.1
        },
    },
    open: {
        backgroundColor: '#07182D',
        clipPath: `circle(140% at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        pointerEvents: 'auto',
        transition: {
            duration: 0.7,
            ease: "easeInOut",
            delay: 0.2
        },
    },
};

  return (
    <motion.div
      className="fixed inset-0 transform-gpu will-change-clip-path z-30"
      variants={variants}
      initial="closed"
      animate="open"
      exit="closed"
    >
        <div className={`w-full font-light
          aspect-16-9:text-72u aspect-16-9:top-443u aspect-16-9:left-146u aspect-16-9:w-486u
          not-aspect-16-9:top-400u not-aspect-16-9:left-1/2 not-aspect-16-9:-translate-x-1/2
          not-aspect-16-9:text-120u not-aspect-16-9:text-center
          absolute transition-opacity duration-300 ${fullScreen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>{ item.title }</div>

        <div 
          className={`bg-white origin-left transition-all duration-500 ease-in-out
            absolute
            ${
            fullScreen 
              ? `w-full h-full rounded-none p-0
                aspect-16-9:bottom-0 aspect-16-9:right-0
                not-aspect-16-9:top-0 not-aspect-16-9:left-0
                z-30`
              : `
                aspect-16-9:w-2938u aspect-16-9:h-1662u aspect-16-9:bottom-108u aspect-16-9:right-106u
                not-aspect-16-9:top-680u not-aspect-16-9:left-480u not-aspect-16-9:w-2646u not-aspect-16-9:h-1497u
                py-51u pl-51u pr-199u rounded-30u`
            }`
          }
        >
            {/* Loading Indicator */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-30u z-10">
                <div className="flex flex-col items-center gap-32u">
                  {/* Spinner */}
                  <div className="w-120u h-120u border-16u border-gray-300 border-t-cyan rounded-full animate-spin"></div>
                </div>
              </div>
            )}

            {!isLoading && !fullScreen && (
            <Button
                onClick={handleExpandToggle}
                className="click-effect absolute top-99u right-44u w-113u h-113u rounded-full focus:outline-none
                flex items-center justify-center
                z-2
                bg-[#A9AAB5]">
                <IconExpand className="w-77u h-77u" color="#FFFFFF" />
            </Button>
            )}
            
            {item.video ? (
              <div className="relative w-full h-full">
                <video
                  className={`w-full h-full bg-black transition-all duration-500 ${
                    fullScreen ? 'rounded-none' : 'rounded-30u'
                  } ${!showVideoOverlay ? 'cursor-pointer' : ''}`}
                  ref={videoRef}
                  src={`${process.env.PUBLIC_URL}/${item.video}`}
                  controlsList="nodownload"
                  preload="metadata"
                  onClick={handleVideoPauseClick}
                  onLoadedMetadata={handleVideoLoad}
                  onCanPlay={handleVideoLoad}
                  onEnded={handleVideoEnded}
                  onError={(e) => {
                    console.error('Error loading video:', e);
                    setIsLoading(false);
                  }}
                  style={{
                      pointerEvents: 'auto',
                      touchAction: 'none',
                      opacity: isLoading ? 0 : 1,
                      transition: 'opacity 0.3s ease-in-out'
                  }}
                />
                {/* Video Overlay con botón Play */}
                {showVideoOverlay && !isLoading && (
                  <motion.div 
                    className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20 ${
                      fullScreen ? 'rounded-none' : 'rounded-30u'
                    }`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      onClick={handleVideoPlayClick}
                      className="w-236u h-236u bg-white bg-opacity-10 rounded-full relative origin-center cursor-pointer"
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <div className="absolute inset-24u bg-white bg-opacity-40 rounded-full flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="3.8125rem"
                          height="4.8125rem"
                          fill="none"
                          viewBox="0 0 57 70"
                          className='ml-16u'
                        >
                          <path
                            fill="#fff"
                            d="M0 5.26v58.86c0 3.898 4.26 6.297 7.592 4.276l46.631-28.274c3.144-1.907 3.224-6.44.15-8.456L7.743 1.079C4.416-1.103 0 1.283 0 5.259"
                          ></path>
                        </svg>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </div>
            ) : (
              <iframe
                className={`w-full h-full bg-black transition-all duration-500 ${
                  fullScreen ? 'rounded-none' : 'rounded-30u'
                }`}
                ref={iframeRef}
                src={item.ppt ?? item.url}
                allowFullScreen
                allow="autoplay; fullscreen; accelerometer; gyroscope; magnetometer; microphone; camera; encrypted-media; picture-in-picture"
                onLoad={handleIframeLoad}
                style={{
                    pointerEvents: 'auto',
                    touchAction: 'none',
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                }}
                >
              </iframe>
            )}

        </div>

        <Button
          onClick={(event) => handleButtonBackClick(event)}
          disabled={fullScreen}
          className={`click-effect absolute py-[2.75rem] pl-66u pr-52u
          left-138u bottom-108u z-10
          w-458u
          justify-between
          text-32u
          bg-[#07182D]
          transition-opacity duration-300 ${
            fullScreen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        >
          <IconArrow className="w-[1.125rem] h-auto transform -scale-x-100" />
          <div>Back to main topic</div>
        </Button>
    </motion.div>
  );
};